import { useEffect } from 'react';
import useTranslation from 'Hooks/useTranslation';

interface TabDetectorConfig {
  /** Función que se ejecutará cuando se detecte un cambio de pestaña */
  onTabSwitch?: () => void;
  /** Función que se ejecutará cuando se intente cerrar la ventana */
  onWindowClose?: () => void;
  /** Indica si el detector está activo */
  isActive?: boolean;
  /** Mostrar alerta al cambiar de pestaña */
  showAlert?: boolean;
  /** Mensaje personalizado para la alerta */
  alertMessage?: string;
  /** Prevenir que se cierre la ventana */
  preventClose?: boolean;
}

/**
 * Hook personalizado que detecta cuando un usuario cambia de pestaña o intenta cerrar la ventana.
 *
 * @example
 * ```tsx
 * // Uso básico
 * useSwitchTabDetector({
 *   onTabSwitch: () => console.log('Usuario cambió de pestaña'),
 *   isActive: true
 * });
 *
 * // Uso en un examen
 * useSwitchTabDetector({
 *   onTabSwitch: finishTest,
 *   onWindowClose: finishTest,
 *   isActive: !isExamFinished,
 *   showAlert: true,
 *   alertMessage: 'No puedes cambiar de pestaña durante el examen',
 *   preventClose: true
 * });
 * ```
 */
const useSwitchTabDetector = ({
  onTabSwitch,
  onWindowClose,
  isActive = true,
  showAlert = false,
  alertMessage,
  preventClose = false,
}: TabDetectorConfig = {}) => {
  const { t2 } = useTranslation('course_test');

  useEffect(() => {
    if (!isActive) {
      return;
    }

    let hasTabSwitched = false;

    const handleTabSwitch = () => {
      if (!hasTabSwitched) {
        hasTabSwitched = true;

        if (showAlert) {
          alert(alertMessage || t2('youChangedTabs'));
        }

        onTabSwitch?.();
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (preventClose) {
        event.preventDefault();
        event.returnValue = '';
      }

      onWindowClose?.();
    };

    window.addEventListener('blur', handleTabSwitch);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('blur', handleTabSwitch);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isActive, onTabSwitch, onWindowClose, showAlert, alertMessage, preventClose, t2]);
};

export default useSwitchTabDetector;
